/**
 * Theme utilities for managing light/dark mode
 */
import { devLog } from "./logger";

/**
 * Detect the user's system theme preference
 * @returns {'light'|'dark'} The system's preferred color scheme
 */
export function detectSystemTheme() {
  if (typeof window === "undefined") return "light";

  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  return prefersDark ? "dark" : "light";
}

/**
 * Apply a theme to the document
 * @param {'light'|'dark'} theme - The theme to apply
 */
export function applyTheme(theme) {
  if (typeof document === "undefined") return;

  const root = document.documentElement;

  if (theme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }

  devLog(`Theme applied: ${theme}`);
}

/**
 * Initialize the theme based on user settings
 * @param {Object} settings - User settings object
 * @param {string} settings.theme - Theme preference ('light', 'dark', or 'auto')
 */
export function initializeTheme(settings) {
  if (typeof window === "undefined") return;

  let themeToApply = "light"; // Default

  if (settings?.theme) {
    if (settings.theme === "auto") {
      // Follow system preference
      themeToApply = detectSystemTheme();
    } else {
      // Use explicit theme setting
      themeToApply = settings.theme;
    }
  }

  applyTheme(themeToApply);

  return themeToApply;
}

/**
 * Set up a listener for system theme changes (for auto mode)
 * @param {Function} callback - Function to call when system theme changes
 * @returns {Function} Cleanup function to remove the listener
 */
export function watchSystemTheme(callback) {
  if (typeof window === "undefined") return () => {};

  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

  const handler = (e) => {
    const newTheme = e.matches ? "dark" : "light";
    callback(newTheme);
  };

  // Modern browsers
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }

  // Fallback for older browsers
  mediaQuery.addListener(handler);
  return () => mediaQuery.removeListener(handler);
}
